import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isNil, mapValues, merge, omit } from 'lodash';
import { Optional } from 'utility-types';
import { v4 as uuid } from 'uuid';
import { apiDashboardServer } from 'api/index';
import { formatPaginationQueryParams } from 'api/utils';
import { DEFAULT_PAGE_SIZE } from 'api/utils/queryParams';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useDeepCompareEffect, useIsMountedRef } from 'shared/hooks/global';
import { NumberSerializer, StringSerializer, UpdateType, useQueryParams } from 'shared/hooks/urlQueryParams';
import { ArraySerializer } from 'shared/hooks/urlQueryParams/paramsSerializer';
import { CommutifiError, GetResponse, RequestParams } from 'shared/interfaces';
import { useQuery, UseQueryOptions } from 'api/reactQuery';
import { QueryParamType } from './constants';
function isGetManyResponse<RecordType>(res: any): res is GetResponse<RecordType> {
  return Object.prototype.hasOwnProperty.call(res, 'records') && Object.prototype.hasOwnProperty.call(res, '_metadata');
}
const paramTypeToSerializer = (paramType: QueryParamType) => {
  switch (paramType) {
    case QueryParamType.String:
      return StringSerializer;
    case QueryParamType.Number:
      return NumberSerializer;
    case QueryParamType.Array:
      return ArraySerializer;
    default:
      return StringSerializer;
  }
};
export const buildListQueryKey = (endpoint: string | undefined, params: object = {}) => ['list', endpoint, omit(params, ['relations', 'fields'])];

/**
 * Handle Query params linked and saved in the URL and triggers the api call
 * and formatting of the response to standardize how we connect data to
 * a List
 */
export function useListController(t0) {
  const $ = _c(63);
  const {
    async,
    endpoint,
    queryParams,
    initUrlQueryParam: t1,
    urlSearchParams,
    queryOptions,
    getListAdapter,
    fixedQueryParams,
    initialQueryParams,
    data,
    fetchOnMount,
    disablePagination,
    pageSize
  } = t0;
  const initUrlQueryParam = t1 === undefined ? {} : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = uuid();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const fallbackApiCallIdentifier = t2;
  const match = useRouteMatch();
  const location = useLocation();
  const isActive = match.url === location.pathname;
  const isMounted = useIsMountedRef();
  const shouldFetchOnMount = !isMounted.current && fetchOnMount;
  const [urlQuery, setUrlQuery] = useQueryParams(merge(mapValues(initUrlQueryParam, paramTypeToSerializer), {
    sort: StringSerializer,
    page: NumberSerializer,
    ...(urlSearchParams || [""]).reduce(_temp, {}),
    ...mapValues(fixedQueryParams, _temp2)
  }));
  const [isUrlParamsInitialized, setIsUrlParamsInitialized] = useState(false);
  let t3;
  if ($[1] !== fixedQueryParams || $[2] !== initialQueryParams || $[3] !== isActive || $[4] !== isUrlParamsInitialized || $[5] !== setUrlQuery || $[6] !== urlQuery) {
    t3 = () => {
      if (!isActive) {
        return;
      }
      const isProperTypes = initialQueryParams && Object.values(initialQueryParams).every(_temp4) || typeof initialQueryParams === "number";
      if (!isProperTypes) {
        setIsUrlParamsInitialized(true);
        return;
      }
      const filteredInitialParams = isUrlParamsInitialized ? {} : Object.keys(initialQueryParams || {}).reduce((acc, key) => {
        if (urlQuery[key] === undefined || urlQuery[key] === null) {
          acc[key] = initialQueryParams[key];
        }
        return acc;
      }, {});
      setUrlQuery({
        ...urlQuery,
        ...filteredInitialParams,
        ...fixedQueryParams
      } as any);
      setIsUrlParamsInitialized(true);
    };
    $[1] = fixedQueryParams;
    $[2] = initialQueryParams;
    $[3] = isActive;
    $[4] = isUrlParamsInitialized;
    $[5] = setUrlQuery;
    $[6] = urlQuery;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== fixedQueryParams || $[9] !== initialQueryParams || $[10] !== isActive) {
    t4 = [initialQueryParams, fixedQueryParams, isActive];
    $[8] = fixedQueryParams;
    $[9] = initialQueryParams;
    $[10] = isActive;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useDeepCompareEffect(t3, t4);
  const t5 = !isActive && shouldFetchOnMount || !isUrlParamsInitialized ? initialQueryParams : urlQuery;
  const t6 = disablePagination ? undefined : pageSize ?? DEFAULT_PAGE_SIZE;
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = formatPaginationQueryParams(t5, {
      limit: t6
    });
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== fixedQueryParams || $[16] !== t7) {
    t8 = {
      ...t7,
      ...fixedQueryParams
    };
    $[15] = fixedQueryParams;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const urlQS = t8;
  let t9;
  if ($[18] !== queryParams || $[19] !== urlQS) {
    t9 = merge({}, urlQS, queryParams);
    $[18] = queryParams;
    $[19] = urlQS;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const qs = t9 as QueryParams & RequestParams;
  let t10;
  if ($[21] !== async || $[22] !== endpoint || $[23] !== fallbackApiCallIdentifier || $[24] !== qs) {
    t10 = buildListQueryKey(endpoint || async?.fn?.toString().match(/\["(.*?)"\]/)?.[1] || `asyncFn_${fallbackApiCallIdentifier}`, {
      ...qs,
      ...async?.params(qs)
    });
    $[21] = async;
    $[22] = endpoint;
    $[23] = fallbackApiCallIdentifier;
    $[24] = qs;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== async || $[27] !== endpoint || $[28] !== getListAdapter || $[29] !== qs) {
    t11 = async () => {
      if (endpoint && endpoint.includes("undefined")) {
        throw Error(`Undefined value(s) in endpoint - Skipping API call to ${endpoint}`);
      }
      const res = async ? await async.fn(...async.params(qs)) : await apiDashboardServer(endpoint || "", {
        method: "get",
        queryParams: qs
      });
      return getListAdapter ? getListAdapter(res) : isGetManyResponse(res) ? res : {
        records: [],
        _metadata: {
          totalCount: 0,
          count: 0,
          offset: 0,
          limit: 0
        }
      };
    };
    $[26] = async;
    $[27] = endpoint;
    $[28] = getListAdapter;
    $[29] = qs;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const t12 = queryOptions?.initialData ?? data;
  const t13 = shouldFetchOnMount || fetchOnMount && isActive || !fetchOnMount && isUrlParamsInitialized && (queryOptions?.enabled ?? Boolean((endpoint || async) && isActive));
  let t14;
  if ($[31] !== queryOptions || $[32] !== t10 || $[33] !== t11 || $[34] !== t12 || $[35] !== t13) {
    t14 = {
      queryKey: t10,
      queryFn: t11,
      ...queryOptions,
      initialData: t12,
      enabled: t13
    };
    $[31] = queryOptions;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const apiRes = useQuery(t14);
  let t15;
  if ($[37] !== isActive || $[38] !== setUrlQuery) {
    t15 = (params_0, t16) => {
      const updateType = t16 === undefined ? UpdateType.PushIn : t16;
      return isActive ? setUrlQuery(params_0, updateType) : undefined;
    };
    $[37] = isActive;
    $[38] = setUrlQuery;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const updateQueryParams = t15;
  const controlledData = data || apiRes.data;
  const totalPages = Math.floor(controlledData?._metadata.totalCount ?? 15 / (data?._metadata.limit ?? 15));
  let t16;
  if ($[40] !== apiRes.isFetching || $[41] !== controlledData?.records || $[42] !== isActive || $[43] !== setUrlQuery || $[44] !== totalPages || $[45] !== urlQuery.page) {
    t16 = () => {
      if (isNil(urlQuery.page) || !isActive) {
        return;
      }
      if (urlQuery.page <= 0 || !apiRes.isFetching && urlQuery.page > 1 && controlledData?.records.length === 0) {
        setUrlQuery({
          page: 1
        }, UpdateType.PushIn);
      } else {
        if (!apiRes.isFetching && totalPages && urlQuery.page > totalPages) {
          setUrlQuery({
            page: totalPages
          }, UpdateType.PushIn);
        }
      }
    };
    $[40] = apiRes.isFetching;
    $[41] = controlledData?.records;
    $[42] = isActive;
    $[43] = setUrlQuery;
    $[44] = totalPages;
    $[45] = urlQuery.page;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== apiRes.isFetching || $[48] !== apiRes.isLoading || $[49] !== controlledData || $[50] !== isActive || $[51] !== setUrlQuery || $[52] !== totalPages || $[53] !== urlQuery) {
    t17 = [urlQuery, totalPages, apiRes.isLoading, setUrlQuery, apiRes.isFetching, controlledData, isActive];
    $[47] = apiRes.isFetching;
    $[48] = apiRes.isLoading;
    $[49] = controlledData;
    $[50] = isActive;
    $[51] = setUrlQuery;
    $[52] = totalPages;
    $[53] = urlQuery;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  useEffect(t16, t17);
  let t18;
  if ($[55] !== fixedQueryParams || $[56] !== urlQuery) {
    t18 = merge(urlQuery, fixedQueryParams);
    $[55] = fixedQueryParams;
    $[56] = urlQuery;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== apiRes || $[59] !== controlledData || $[60] !== t18 || $[61] !== updateQueryParams) {
    t19 = {
      ...apiRes,
      data: controlledData,
      query: t18,
      setQuery: updateQueryParams
    };
    $[58] = apiRes;
    $[59] = controlledData;
    $[60] = t18;
    $[61] = updateQueryParams;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  return t19;
}
function _temp4(q) {
  return Array.isArray(q) && q.every(_temp3) || typeof q === "string";
}
function _temp3(qi) {
  return typeof qi === "string" || typeof qi === "number";
}
function _temp2(value) {
  if (Array.isArray(value)) {
    return ArraySerializer;
  }
  return StringSerializer;
}
function _temp(params, paramName) {
  return {
    ...params,
    [paramName]: StringSerializer
  };
}
export interface UseListControllerProps<T extends (...args: any) => Promise<ApiResponse>, QueryParams, ApiResponse, RecordType> {
  /**
   * Option 1 to know which endpoint to call to get the List data is
   * to pass an async fn + queryParams (optional) that wraps your API call.
   * qs: queryParams + initialQueryParams defined
   *
   * Option 2 is to define:
   * endpoint + queryParams (optional) + getListAdapter (optional, mostly for legacy endpoints)
   */
  async?: {
    fn: T;
    params: (qs?: QueryParams & RequestParams) => [...Parameters<T>];
  };
  /**
   * Endpoint path to call our default server (api-dashboard)
   */
  endpoint?: string;
  /**
   * To support Legacy Get Many endpoints or simply support unconventional
   * api response, this function is used to format properly your expected
   * api response to return a standardized Result
   */
  getListAdapter?: (data: Awaited<ApiResponse>) => GetResponse<RecordType> | Promise<GetResponse<RecordType>>;
  /**
   * Extra query parameters to join to the call. Those are added on top
   * of the ones handled in the internal state like pagination and filters
   */
  queryParams?: QueryParams & RequestParams;
  /**
   * Initializer object for URL query parameter. Defines one or multiple key-value
   * with name of param and serializer to user (Number, Array, String, etc.)
   * This is useful when controlling extra parameters from outside the List context
   */
  initUrlQueryParam?: Record<string, QueryParamType>;
  /**
   * Sounds like 'initUrlQueryParam' but it's not. It's not an initializer of how
   * to serialize the params. This is an actual initial setter of query parameters
   * in the URL.
   *
   * @example \{ status: active \} will set <URL>?status='active'
   */
  initialQueryParams?: Record<string, unknown>;
  /**
   * React Query options (See their doc for more details)
   */
  queryOptions?: Optional<UseQueryOptions<GetResponse<RecordType>, CommutifiError>, 'queryKey'>;
  /**
   * Allow to define what are the different search parameters that should be
   * maintained internally.
   * @example ['%:name']
   */
  urlSearchParams?: string[];
  /**
   * Filters to permanently apply. They will be applied on top of
   * other filters
   */
  fixedQueryParams?: Record<string, unknown>;
  /**
   * Controlled data if you already have the data and don't need to link
   * the List to an endpoint. In that case you also need to make sure the endpoint
   * prop is not passed
   */
  data?: GetResponse<RecordType>;
  /**
   * Fetch on List mounting, weather the URL served is the List view or
   * the detail view doesn't matter
   */
  fetchOnMount?: boolean;
  /**
   * Avoid sending limits and offsets to the list API call
   */
  disablePagination?: boolean;
  /**
   * Number of records to fetch and display per page. Defaults to DEFAULT_PAGE_SIZE.
   * Note this can't be set through 'initialQueryParams' because the limit is not
   * a URL query parameter, it only lives in the API call.
   *
   * Precedence: 'disablePagination' wins over it (no limit is sent at all), and a
   * 'limit' set in the 'queryParams' prop wins over it too, but that one leaves the
   * offsets computed from DEFAULT_PAGE_SIZE, so prefer this prop.
   */
  pageSize?: number;
}